import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { CheckCircle2, Lock, Download, ArrowRight, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const COVER = "https://static.prod-images.emergentagent.com/jobs/2e360db4-79c7-4489-9f27-5d85af163a96/images/b43c371e8463ba141c8605960e0db1a95b47346879a778d3926e2f7807500787.jpeg";

const BULLETS = [
  "The exact roadmap we use to take clients from denied to approved",
  "How to read your credit report like an underwriter does",
  "Which negative items to attack first — and which to leave alone",
  "The credit-building moves that raise scores the fastest",
  "How to walk into a lender's office fully prepared",
];

export default function Ebook() {
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const buy = async () => {
    setError("");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setError("Enter a valid email — your download link is sent there.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/ebook/checkout", { email: email.trim(), origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't start checkout. Please try again.");
      setBusy(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>From Denied to Approved — $4.99 Ebook | Infantry Financial</title>
        <meta name="description" content="The Credit Restoration Roadmap: a step-by-step ebook that takes you from denied to approved. Instant download for $4.99." />
        <link rel="canonical" href="https://infantry-financial.com/ebook" />
        <meta property="og:title" content="From Denied to Approved — Credit Restoration Roadmap" />
        <meta property="og:image" content={COVER} />
      </Helmet>

      <section className="bg-[#F5F4EF] py-20 md:py-28">
        <div className="container-if grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div className="order-2 lg:order-1">
            <span className="chip mb-8" data-testid="ebook-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-[#3F4A1D] inline-block" />
              THE EBOOK
            </span>
            <h1 className="font-hero text-[#23261C] mb-6" style={{ fontSize: "clamp(40px, 5vw, 68px)" }}>
              FROM DENIED<br /><span className="text-[#5C6B21]">TO APPROVED</span>
            </h1>
            <p className="text-[#4A4E40] text-lg leading-relaxed mb-8 max-w-lg" style={{ fontFamily: "Inter, sans-serif" }}>
              The Credit Restoration Roadmap — the same step-by-step system we use with paying clients, in one straight-talking guide.
            </p>

            <ul className="space-y-3 mb-10">
              {BULLETS.map((b) => (
                <li key={b} className="flex items-start gap-3 text-[#3A3D32] text-sm" style={{ fontFamily: "Inter, sans-serif" }}>
                  <CheckCircle2 className="w-4.5 h-4.5 w-5 h-5 text-[#4F5D1E] shrink-0 mt-0.5" /> {b}
                </li>
              ))}
            </ul>

            <div className="bg-white border border-[#23261C]/10 rounded-2xl p-6 max-w-md">
              <div className="flex items-baseline gap-2 mb-4">
                <span className="font-bebas text-4xl text-[#23261C]" style={{ letterSpacing: "0.03em" }}>$4.99</span>
                <span className="text-[#5B5F50] text-xs uppercase tracking-widest" style={{ fontFamily: "Inter, sans-serif" }}>one-time · instant download</span>
              </div>
              <input
                type="email"
                className="if-input mb-3"
                placeholder="Your email (download link goes here)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="ebook-email"
              />
              {error && <div className="text-xs text-red-600 mb-3" style={{ fontFamily: "Inter, sans-serif" }} data-testid="ebook-error">{error}</div>}
              <button onClick={buy} disabled={busy} className="btn-gold w-full disabled:opacity-60" data-testid="ebook-buy-btn">
                {busy ? "Redirecting to secure checkout…" : <>Get the Ebook <ArrowRight className="w-4 h-4" /></>}
              </button>
              <div className="flex items-center gap-2 mt-4 text-[11px] text-[#5B5F50]" style={{ fontFamily: "Inter, sans-serif" }}>
                <Lock className="w-3.5 h-3.5 text-[#4F5D1E]" /> Secure Stripe checkout · Personal licensed copy · Delivered instantly by email
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2 flex justify-center">
            <img src={COVER} alt="From Denied to Approved — ebook cover" className="w-full max-w-sm rounded-2xl shadow-2xl" data-testid="ebook-cover" />
          </div>
        </div>
      </section>

      <section className="bg-[#EFEDE5] py-14">
        <div className="container-if flex flex-wrap justify-center gap-10 text-center">
          {[{ icon: Download, t: "Instant delivery", s: "Download link on-screen and in your inbox" },
            { icon: ShieldCheck, t: "Licensed to you", s: "Every page is stamped with your name — your personal copy" },
            { icon: Lock, t: "CROA compliant", s: "Real strategy, no gimmicks, no illegal tactics" }].map((f) => (
            <div key={f.t} className="max-w-[240px]">
              <f.icon className="w-6 h-6 text-[#4F5D1E] mx-auto mb-3" />
              <p className="text-[#23261C] font-semibold text-sm mb-1" style={{ fontFamily: "Inter, sans-serif" }}>{f.t}</p>
              <p className="text-[#5B5F50] text-xs leading-relaxed" style={{ fontFamily: "Inter, sans-serif" }}>{f.s}</p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
